import {
  faAt,
  faCheck,
  faExclamationTriangle,
  faLink,
  faLock,
  faMinus,
  faPhoneAlt,
  faPlus,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-light-svg-icons';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  EventEmitter,
  ViewRef,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormControl, AbstractControl } from '@angular/forms';

export interface InputData {
  value: string | number;
  valid: boolean;
}

@Component({
  selector: 'app-input',
  styleUrls: ['./input.component.scss'],
  templateUrl: './input.component.html',
})
export class InputComponent implements OnInit {
  // Constants
  inputTypes = {
    TEXT: 'text',
    URL: 'url',
    EMAIL: 'email',
    PASSWORD: 'password',
    TEL: 'tel',
    NUMBER: 'number',
    TEXTAREA: 'textarea',
    DATE: 'date',
    TIME: 'time',
    LIMIT: 'limit',
  };
  inputSizes = {
    SMALL: 'form-control-sm',
    LARGE: 'form-control-lg',
  };
  defaultLabels = {
    TEXT: 'Enter text',
    URL: 'Enter URL',
    EMAIL: 'Enter email address',
    PASSWORD: 'Enter password',
    TEL: 'Enter mobile number',
    NUMBER: 'Enter or select number',
    TEXTAREA: 'Write comments',
    LIMIT: 'e.g £ 4,500',
  };
  icons = {
    check: faCheck,
    error: faExclamationTriangle,
    url: faLink,
    email: faAt,
    password: faLock,
    tel: faPhoneAlt,
    plus: faPlus,
    minus: faMinus,
    eye: faEye,
    eyeClose: faEyeSlash,
  };
  //UI Variables
  selectedType: string = '';
  size: string = '';
  class: string = '';
  showPwd = false;

  @ViewChild('input') input: ElementRef;

  // Inputs
  @Input() label: string;
  @Input() min: string;
  @Input() max: string;
  @Input() value: string | number;
  @Input() caption: string;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() required: boolean;
  @Input() pattern: string | RegExp;
  @Input() formCtrlName: any;
  @Input() formGrp: FormGroup;
  @Input() prefixIcon: any;
  @Input() className: string;
  @Output() @Input() model: EventEmitter<any>;
  @Input() control!: FormControl;

  //Outputs
  @Output() valueChanged: EventEmitter<InputData> = new EventEmitter();
  @Output() changeEvent: EventEmitter<InputData> = new EventEmitter();

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    this.setType();
    this.setSize();
    this.setLabels();
    this.applyClass();
    this.control?.valueChanges.subscribe(v => {
    this.value = v;   // binds your display
  });
  }
  setType() {
    if ((this.el.nativeElement as HTMLElement).hasAttribute('text')) {
      this.selectedType = this.inputTypes.TEXT;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('url')) {
      this.selectedType = this.inputTypes.URL;
      this.pattern = !this.pattern
        ? /^(https?|ftp|torrent|image|irc):\/\/(-\.)?([^\s\/?\.#-]+\.?)+(\/[^\s]*)?$/i
        : this.pattern;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('email')) {
      this.selectedType = this.inputTypes.EMAIL;
      this.pattern = !this.pattern
        ? /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        : this.pattern;
    } else if (
      (this.el.nativeElement as HTMLElement).hasAttribute('password')
    ) {
      this.selectedType = this.inputTypes.PASSWORD;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('tel')) {
      this.selectedType = this.inputTypes.TEL;
    } else if (
      (this.el.nativeElement as HTMLElement).hasAttribute('inputNumber')
    ) {
      this.selectedType = 'inputNumber';
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('number')) {
      this.selectedType = this.inputTypes.NUMBER;
      this.value = this.value ? this.value : 0;
    } else if (
      (this.el.nativeElement as HTMLElement).hasAttribute('textarea')
    ) {
      this.selectedType = this.inputTypes.TEXTAREA;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('date')) {
      this.selectedType = this.inputTypes.DATE;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('time')) {
      this.selectedType = this.inputTypes.TIME;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('limit')) {
      this.selectedType = this.inputTypes.LIMIT;
    }
  }
  toggleInputType(condition: boolean) {
    this.showPwd = condition;
  }
  setSize() {
    if ((this.el.nativeElement as HTMLElement).hasAttribute('sm')) {
      this.size = this.inputSizes.SMALL;
    } else if ((this.el.nativeElement as HTMLElement).hasAttribute('lg')) {
      this.size = this.inputSizes.LARGE;
    }
  }

  setLabels() {
    if (!this.label) {
      switch (this.selectedType) {
        case this.inputTypes.TEXTAREA:
          this.label = this.defaultLabels.TEXTAREA;
          break;
        case this.inputTypes.EMAIL:
          this.label = this.defaultLabels.EMAIL;
          break;
        case this.inputTypes.URL:
          this.label = this.defaultLabels.URL;
          break;
        case this.inputTypes.PASSWORD:
          this.label = this.defaultLabels.PASSWORD;
          break;
        case this.inputTypes.NUMBER:
          this.label = this.defaultLabels.NUMBER;
          break;
        case this.inputTypes.TEL:
          this.label = this.defaultLabels.TEL;
          break;
        case this.inputTypes.LIMIT:
          this.label = this.defaultLabels.LIMIT;
          break;
        case this.inputTypes.TEXT:
        default:
          this.label = this.defaultLabels.TEXT;
          break;
      }
    }
  }

  applyClass() {
    this.class = `form-control ${this.size}`;
  }
  get getControl(): any {
    return this.formGrp.get(this.formCtrlName);
  }

  increment() {
    //convert back to number
    this.value = Number(this.value);
    if (Number.isFinite(this.value)) {
      this.value = Number(this.value) + 1;
    }
    this.setFormValue();
  }
  decrement() {
    //convert back to number
    this.value = Number(this.value);
    if (this.value > 0 && Number.isFinite(this.value)) {
      this.value = Number(this.value) - 1;
    }
    this.setFormValue();
  }

  changed(valid: boolean | null) {
    this.valueChanged.emit({
      value: this.value,
      valid: valid as boolean,
    });
  }
  setFormValue() {
    if (this.formGrp && this.formCtrlName) {
      this.formGrp.get(this.formCtrlName)?.setValue(this.value);
    }
  }

  changeInputValue(event: any) {
    this.changeEvent.emit(event);
  }
}
